import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

/**
 * Public page reached via the magic link sent to the user's inbox.
 * - Validates the token on mount.
 * - On submit, consumes the token and clears must_change_password.
 * - All sessions are invalidated server-side, so we redirect to /login.
 */
export default function ResetPassword() {
  const t = useT();
  const { token } = useParams();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [info, setInfo] = useState({ email: "", name: "" });
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const strength = (() => {
    if (!next) return { label: "", pct: 0, color: "#6B7280" };
    const score = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(next)).length + (next.length >= 10 ? 1 : 0);
    if (score <= 2) return { label: t("pwd.strength.weak"), pct: 30, color: "#DC2626" };
    if (score <= 4) return { label: t("pwd.strength.ok"), pct: 65, color: "#F4B81F" };
    return { label: t("pwd.strength.strong"), pct: 100, color: "#1B2E7A" };
  })();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/auth/reset-password/${encodeURIComponent(token)}`);
        if (!cancelled) {
          setValid(true);
          setInfo({ email: r.data?.email || "", name: r.data?.name || "" });
        }
      } catch (err) {
        if (!cancelled) {
          setValid(false);
          setValidationError(err.response?.data?.detail || t("reset.err.invalid"));
        }
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error(t("pwd.err.too_short"));
      return;
    }
    if (next !== confirm) {
      toast.error(t("pwd.err.mismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      toast.success(t("reset.ok"));
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("reset.err.generic"));
    } finally {
      setBusy(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-6" data-testid="reset-validating">
        <div className="inline-flex items-center gap-2 text-sm text-[#374151]">
          <Loader2 className="h-4 w-4 animate-spin text-[#1B2E7A]" />
          {t("reset.validating")}
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-6" data-testid="reset-invalid">
        <div className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-xl p-8">
          <div className="h-10 w-10 rounded-full bg-[#DC2626]/15 flex items-center justify-center mb-3">
            <AlertCircle className="h-5 w-5 text-[#DC2626]" strokeWidth={1.6} />
          </div>
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-2">{t("reset.invalid.title")}</h1>
          <p className="text-sm text-[#374151] leading-relaxed">{validationError}</p>
          <div className="mt-6 flex items-center justify-between">
            <Link to="/forgot-password" className="text-sm text-[#1B2E7A] hover:underline" data-testid="reset-request-new">
              {t("reset.request_new")}
            </Link>
            <Link to="/login" className="text-xs text-[#6B7280] hover:text-[#374151]">
              {t("forgot.go_login")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-xl p-8">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-5 w-5 text-[#1B2E7A]" strokeWidth={1.6} />
          <h1 className="font-heading text-2xl text-[#1A1A1A]">{t("reset.title")}</h1>
        </div>
        <p className="text-xs text-[#6B7280] mb-6">{info.name} · {info.email}</p>

        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <label className="block text-xs text-[#374151] mb-1" htmlFor="reset-new">
              {t("pwd.new")}
            </label>
            <Input
              id="reset-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              data-testid="reset-new"
              required
              autoFocus
            />
            {next && (
              <div className="mt-1.5">
                <div className="h-1 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${strength.pct}%`, backgroundColor: strength.color }} />
                </div>
                <p className="text-[10px] mt-1" style={{ color: strength.color }}>{strength.label}</p>
              </div>
            )}
            <p className="text-[10px] text-[#6B7280] mt-1">{t("pwd.new.hint")}</p>
          </div>
          <div>
            <label className="block text-xs text-[#374151] mb-1" htmlFor="reset-confirm">
              {t("pwd.confirm")}
            </label>
            <Input
              id="reset-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="reset-confirm"
              required
            />
            {confirm && confirm === next && (
              <p className="text-[10px] text-[#1B2E7A] mt-1 inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> {t("pwd.match_ok")}
              </p>
            )}
          </div>
          <Button
            type="submit"
            disabled={busy || !next || !confirm}
            className="w-full bg-[#1B2E7A] hover:bg-[#13205A] text-white"
            data-testid="reset-submit"
          >
            {busy ? t("pwd.saving") : t("reset.submit")}
          </Button>
        </form>
        <p className="text-[10px] text-[#6B7280] mt-4 leading-snug">{t("reset.notice")}</p>
      </div>
    </div>
  );
}
